import { useState, useEffect, useCallback } from 'react';
import { getModuleSettings, updateModuleSettings, type Module } from '../lib/modules';
import { DEFAULT_MODULES } from '../lib/modules';
import { useErrorHandler } from './useErrorHandler';

export function useModules() {
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const { handleError } = useErrorHandler();

  const fetchModules = useCallback(async () => {
    try {
      setLoading(true);
      try {
        const data = await getModuleSettings();
        setModules(data);
      } catch (err) {
        handleError(err, { action: 'fetchModules' });
        setModules(DEFAULT_MODULES);
      }
    } catch (err) {
      handleError(err, { action: 'fetchModules' });
      setModules(DEFAULT_MODULES);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  async function toggleModule(moduleId: string) {
    try {
      const updatedModules = modules.map(module =>
        module.id === moduleId ? { ...module, enabled: !module.enabled } : module
      );
      await updateModuleSettings(updatedModules);
      setModules(updatedModules);
    } catch (err) {
      handleError(err, { action: 'toggleModule', moduleId });
      // Revert changes in memory
      setModules(modules);
    }
  }

  function isModuleEnabled(moduleId: string): boolean {
    const module = modules.find(m => m.id === moduleId);
    // Default to enabled if module not found or loading
    return loading ? true : (module?.enabled ?? true);
  }

  return {
    modules,
    loading,
    toggleModule,
    isModuleEnabled,
  };
}